import { useEffect, useRef } from 'react';
import L from 'leaflet';
import type { DayDot, PhoneHistoryEntry } from './types';
import { dotStyle } from './dotStyle';
import { cartoTileUrl, CARTO_ATTRIBUTION, CARTO_SUBDOMAINS } from './basemap';

interface MapViewProps {
  dots: DayDot[] | null;
  onDotClick: (phoneId: number) => void;
  selectedPhoneId: number | null;
  selectedHistory: PhoneHistoryEntry[] | null;
  showCentralOpen: boolean;
  showColoredDots: boolean;
  originStateFilter: string;
}

// Dim factor applied to non-selected markers when a phone is selected.
const DIM_FACTOR = 0.2;

// Named Leaflet panes. Both panes get their own L.canvas renderer so
// click/hover hit-testing on the dots pane never even considers the
// history polyline / waypoint circles underneath. The history pane
// sits *below* the dots pane (lower z-index) so stray events can
// never reach history layers first.
const DOTS_PANE = 'rdm-dots-pane';
const HISTORY_PANE = 'rdm-history-pane';
const DOTS_PANE_Z = 450; // default overlayPane is 400, markerPane 600
const HISTORY_PANE_Z = 440;

// Bounding box for the lower-48 contiguous USA. Intentionally a tight
// visual crop — wide enough to show the Florida Keys and the Maine
// coastline but tight enough that Mexico, Canada, and the Atlantic take
// up very little of the viewport. Alaska and Hawaii are intentionally
// excluded. Used for the initial auto-fit and for resize-driven refit
// before the user has interacted with the map (see `userInteractedRef`).
const LOWER_48_BOUNDS: L.LatLngBoundsLiteral = [
  [24.396308, -124.848974], // SW: just below the Florida Keys / SW California
  [49.384358, -66.885444],  // NE: Maine / northern border
];

export function MapView({
  dots,
  onDotClick,
  selectedPhoneId,
  selectedHistory,
  showCentralOpen,
  showColoredDots,
  originStateFilter,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  // Two dedicated canvas renderers, one per pane. Keeping them in
  // separate canvases is the key to reliable hit-testing: Leaflet's
  // canvas renderer scans _drawFirst for interactive layers; if a
  // history circleMarker ever ends up on the same canvas as the
  // clickable dots, any pointer handling bug involving the internal
  // _hoveredLayer reference can silently block clicks. Separation
  // eliminates that entire class of failure.
  const dotsRendererRef = useRef<L.Canvas | null>(null);
  const historyRendererRef = useRef<L.Canvas | null>(null);
  const dotLayerRef = useRef<L.LayerGroup | null>(null);
  const historyLayerRef = useRef<L.LayerGroup | null>(null);
  // phone_id -> marker. Persistent across selection toggles so we never
  // destroy + recreate clickable markers just because the user picked
  // one (the destroy-recreate cycle was the v5 bug root cause — see
  // docstring on the dots useEffect below).
  const markersByPhoneIdRef = useRef<Map<number, L.CircleMarker>>(new Map());
  // Kept in a ref so the ref-stable click handler can read the latest
  // callback without being part of the useEffect dep list.
  const onDotClickRef = useRef(onDotClick);
  onDotClickRef.current = onDotClick;
  // Becomes true after the first user zoom/pan so we do not fight them
  // by refitting on window resize. Date/playback changes never refit.
  const userInteractedRef = useRef<boolean>(false);

  // Initialize map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: [39.5, -98.35],
      zoom: 4,
      minZoom: 3,
      maxZoom: 11,
      worldCopyJump: false,
      preferCanvas: true,
      zoomControl: true,
      attributionControl: true,
    });

    // Create dedicated panes so the history overlay is layered *below*
    // the dots. The dots pane gets its own canvas so hit-testing is
    // limited to the clickable dots; the history pane is created with
    // `interactive: false` on every shape anyway, but putting them in a
    // lower pane is defense in depth.
    const historyPane = map.createPane(HISTORY_PANE);
    historyPane.style.zIndex = String(HISTORY_PANE_Z);
    historyPane.style.pointerEvents = 'none';
    const dotsPane = map.createPane(DOTS_PANE);
    dotsPane.style.zIndex = String(DOTS_PANE_Z);
    // pointer-events on the dots pane must stay auto: the canvas
    // receives mouse events and dispatches them to interactive
    // circleMarkers. (The canvas itself sets pointer-events: auto via
    // Leaflet's default styles.)

    // Fit the lower 48 to the current container size.
    map.fitBounds(LOWER_48_BOUNDS, { padding: [8, 8], animate: false });

    const onUserInteract = () => { userInteractedRef.current = true; };
    map.on('dragstart', onUserInteract);
    map.on('zoomstart', onUserInteract);

    const onWinResize = () => {
      const m = mapRef.current;
      if (!m) return;
      m.invalidateSize({ animate: false });
      if (!userInteractedRef.current) {
        m.fitBounds(LOWER_48_BOUNDS, { padding: [8, 8], animate: false });
      }
    };
    window.addEventListener('resize', onWinResize);
    const raf = window.requestAnimationFrame(() => {
      map.invalidateSize({ animate: false });
      if (!userInteractedRef.current) {
        map.fitBounds(LOWER_48_BOUNDS, { padding: [8, 8], animate: false });
      }
    });

    // CARTO basemap. The API key comes from VITE_CARTO_API_KEY at build
    // time (GitHub secret CARTO_API_KEY in the deploy job); see basemap.ts.
    L.tileLayer(cartoTileUrl(import.meta.env.VITE_CARTO_API_KEY), {
      attribution: CARTO_ATTRIBUTION,
      subdomains: CARTO_SUBDOMAINS,
      maxZoom: 19,
    }).addTo(map);

    // Two independent canvases, each pinned to its pane. Padding lets
    // offscreen dots render so panning reveals them without a flicker.
    dotsRendererRef.current = L.canvas({ padding: 0.5, pane: DOTS_PANE });
    historyRendererRef.current = L.canvas({ padding: 0.5, pane: HISTORY_PANE });
    dotLayerRef.current = L.layerGroup().addTo(map);
    historyLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      window.removeEventListener('resize', onWinResize);
      window.cancelAnimationFrame(raf);
      map.off('dragstart', onUserInteract);
      map.off('zoomstart', onUserInteract);
      map.remove();
      mapRef.current = null;
      dotsRendererRef.current = null;
      historyRendererRef.current = null;
      dotLayerRef.current = null;
      historyLayerRef.current = null;
      markersByPhoneIdRef.current.clear();
    };
  }, []);

  // Redraw the main dot layer whenever the DATA inputs change.
  //
  // Dependencies are intentionally LIMITED to [dots, showCentralOpen,
  // originStateFilter]. `selectedPhoneId` is NOT a dependency here —
  // applying selection-driven dim/highlight by destroying and
  // recreating every marker on every click was the v5 production bug:
  // when Leaflet's canvas renderer had a `_hoveredLayer` reference to
  // a marker that got cleared mid-click, the `.leaflet-interactive`
  // class and the grab cursor stayed stuck, and subsequent marker
  // clicks silently failed (the canvas never re-registered hover
  // targets until mouse was moved out of the stale region).
  //
  // Instead we keep marker *identity* stable across selection toggles
  // and apply selection styling via `setStyle()` in a separate effect
  // below.
  useEffect(() => {
    const dotLayer = dotLayerRef.current;
    const renderer = dotsRendererRef.current;
    if (!dotLayer || !renderer) return;

    dotLayer.clearLayers();
    markersByPhoneIdRef.current.clear();
    if (!dots || dots.length === 0) return;

    for (const dot of dots) {
      const isCentral = dot.central_logistics === 1;

      // Visibility toggles: `showColoredDots` governs filled non-central
      // markers; `showCentralOpen` governs the neutral hollow central
      // rings. Both may independently be off — the map then renders an
      // empty dot layer but the controls and overlays remain usable.
      if (isCentral && !showCentralOpen) continue;
      if (!isCentral && !showColoredDots) continue;
      if (originStateFilter && dot.origin_state !== originStateFilter) continue;

      const style = dotStyle(dot);

      // Central-logistics phones: render ONLY the single neutral hollow
      // ring at the centroid (via `dotStyle`). The per-location
      // `central_locations` array is intentionally NOT drawn on the main
      // map: those secondary markers were filled with the phone color,
      // which broke the "one color per state on a given day" invariant
      // for phones whose same-day points span multiple states. The data
      // is still on the DayDot for the phone-detail view to use. Do not
      // reintroduce these markers or any connector polyline here.

      const marker = L.circleMarker([dot.lat, dot.lon], {
        renderer,
        pane: DOTS_PANE,
        ...style,
        interactive: true,
        bubblingMouseEvents: false,
      });

      const logisticsLine = dot.logistics_flag
        ? '<div style="color:#f1c4bf">logistics</div>'
        : '';
      const centralHeader = isCentral
        ? `<div style="color:#ffd166">central logistics phone</div>
           <div>${dot.central_locations_count ?? '?'} locations on ${escapeHtml(dot.last_seen)}</div>`
        : '';
      marker.bindTooltip(
        `<div style="font: 11px system-ui; line-height:1.4">
          <div><strong>#${dot.phone_id}</strong> ${dot.star ? '&#9733;'.repeat(dot.star) : ''}</div>
          ${centralHeader}
          <div>${[dot.city, dot.state].filter((v) => !!v).map((v) => escapeHtml(String(v))).join(', ')}</div>
          <div>last seen ${escapeHtml(dot.last_seen)} (stale ${dot.days_stale}d)</div>
          ${logisticsLine}
        </div>`,
        { direction: 'top', offset: [0, -4], sticky: true, opacity: 0.95 }
      );

      // Ref-stable click: the handler reads the latest callback from
      // onDotClickRef. Recreating markers whenever React hands us a
      // new `onDotClick` identity would reintroduce the v5 churn bug.
      marker.on('click', () => {
        onDotClickRef.current(dot.phone_id);
      });
      dotLayer.addLayer(marker);
      markersByPhoneIdRef.current.set(dot.phone_id, marker);
    }
  }, [dots, showCentralOpen, showColoredDots, originStateFilter]);

  // Apply selection-driven styling to already-existing markers.
  // This effect NEVER adds, removes, or clears markers. It only calls
  // `setStyle()` on markers that are already on the map, which mutates
  // their options in place and triggers a single canvas redraw. Because
  // no marker identity churns, Leaflet's internal _hoveredLayer
  // reference cannot go stale here.
  useEffect(() => {
    const markers = markersByPhoneIdRef.current;
    if (markers.size === 0) return;

    if (selectedPhoneId === null) {
      // Nothing selected → restore every marker to its undimmed style.
      // We stash the original style on the marker at creation time is
      // unnecessary because `dotStyle()` is pure and we can recompute
      // it from the options, but the simplest correct thing is to
      // just lift the dim: zero dim means opacity/fillOpacity go back
      // to whatever we already have on options, which we preserve by
      // tagging each marker with its base values on creation (below).
      for (const m of markers.values()) {
        const base = (m as any)._baseStyle as { opacity: number; fillOpacity: number } | undefined;
        if (base) m.setStyle({ opacity: base.opacity, fillOpacity: base.fillOpacity });
      }
    } else {
      for (const [phoneId, m] of markers) {
        const base = (m as any)._baseStyle as { opacity: number; fillOpacity: number } | undefined;
        if (!base) continue;
        if (phoneId === selectedPhoneId) {
          m.setStyle({ opacity: base.opacity, fillOpacity: base.fillOpacity });
          m.bringToFront();
        } else {
          m.setStyle({
            opacity: base.opacity * DIM_FACTOR,
            fillOpacity: base.fillOpacity * DIM_FACTOR,
          });
        }
      }
    }
  }, [selectedPhoneId, dots]);

  // Stash base opacities on every newly created marker so the selection
  // effect above can restore them without re-running dotStyle. Runs
  // whenever the marker set changes (i.e. whenever the dots effect ran).
  useEffect(() => {
    for (const m of markersByPhoneIdRef.current.values()) {
      const opts = (m as any).options;
      (m as any)._baseStyle = {
        opacity: opts.opacity,
        fillOpacity: opts.fillOpacity,
      };
    }
  }, [dots, showCentralOpen, showColoredDots, originStateFilter]);

  // Draw the selected phone's history as a polyline and highlighted
  // waypoints. All history layers:
  //   (a) live in the dedicated HISTORY_PANE, which is below the dots
  //       pane in z-order AND has pointer-events: none on the DOM
  //       element;
  //   (b) are marked `interactive: false` on the Leaflet options so
  //       the canvas hit-tester ignores them entirely;
  //   (c) render on a completely separate L.canvas from the dots, so
  //       there is no way for Leaflet's internal _hoveredLayer to
  //       confuse a history waypoint with a clickable dot.
  useEffect(() => {
    const historyLayer = historyLayerRef.current;
    const renderer = historyRendererRef.current;
    if (!historyLayer || !renderer) return;
    historyLayer.clearLayers();
    if (!selectedPhoneId || !selectedHistory || selectedHistory.length === 0) return;

    const points: L.LatLngExpression[] = selectedHistory.map((h) => [h.lat, h.lon]);
    const color = selectedHistory[selectedHistory.length - 1]?.color || '#ffffff';

    if (points.length > 1) {
      L.polyline(points, {
        color,
        weight: 2,
        opacity: 0.85,
        dashArray: '4,4',
        interactive: false,
        bubblingMouseEvents: true,
        pane: HISTORY_PANE,
        renderer,
      }).addTo(historyLayer);
    }

    selectedHistory.forEach((h, idx) => {
      const isLatest = idx === selectedHistory.length - 1;
      L.circleMarker([h.lat, h.lon], {
        renderer,
        pane: HISTORY_PANE,
        radius: isLatest ? 6 : 4,
        color: '#ffffff',
        weight: isLatest ? 2 : 1,
        opacity: 1,
        fillColor: h.color,
        fillOpacity: 1,
        interactive: false,
        bubblingMouseEvents: true,
      }).addTo(historyLayer);
    });
  }, [selectedPhoneId, selectedHistory]);

  // v17: the Leaflet map owns #map-root. The DeliverFund watermark is
  // rendered by App.tsx as a sibling of .playback-bar inside .main, so it
  // stacks ABOVE the playback bar and Leaflet's controls. Keeping the
  // watermark outside the map-stage avoids the v16 failure where the
  // bottom-right mark was occluded by the (absolute, full-width) playback
  // bar and Leaflet's own attribution control.
  return (
    <div className="map-stage">
      <div id="map-root" data-testid="map-root" ref={containerRef} />
    </div>
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
